/**
 * ReviewComments Component
 *
 * Displays and manages review comments from building department reviewers.
 * Features:
 * - List of comments organized by review cycle
 * - Status tracking (open, in_progress, resolved)
 * - Assignment to team members
 * - Filtering and sorting
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MessageSquare,
  Plus,
  Filter,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  MoreVertical,
  User,
  FileText,
  Pencil,
  Trash2,
  Building2,
  Flame,
  MapPin,
  Wrench,
  Leaf,
  Zap,
  Search,
  Mail,
} from 'lucide-react';
import type {
  ReviewComment,
  ReviewCommentSource,
  ReviewCommentCategory,
  ReviewCommentStatus,
} from '@/types/jurisdiction';

interface ReviewCommentsProps {
  comments: ReviewComment[];
  onAddComment?: () => void;
  onImportEmail?: () => void;
  onUpdateStatus?: (commentId: string, status: ReviewCommentStatus) => void;
  onAssign?: (commentId: string, userId: string) => void;
  onEdit?: (comment: ReviewComment) => void;
  onDelete?: (commentId: string) => void;
  teamMembers?: Array<{ id: string; name: string }>;
  readOnly?: boolean;
}

const sourceIcons: Record<ReviewCommentSource, typeof Building2> = {
  building_dept: Building2,
  fire_marshal: Flame,
  zoning: MapPin,
  engineering: Wrench,
  environmental: Leaf,
  utilities: Zap,
  hoa: Building2,
  other: MessageSquare,
};

const sourceLabels: Record<ReviewCommentSource, string> = {
  building_dept: 'Building Dept',
  fire_marshal: 'Fire Marshal',
  zoning: 'Zoning',
  engineering: 'Engineering',
  environmental: 'Environmental',
  utilities: 'Utilities',
  hoa: 'HOA',
  other: 'Other',
};

const categoryLabels: Record<ReviewCommentCategory, string> = {
  plans_correction: 'Plans Correction',
  missing_document: 'Missing Document',
  code_violation: 'Code Violation',
  clarification_needed: 'Clarification Needed',
  fee_issue: 'Fee Issue',
  site_issue: 'Site Issue',
  approval_condition: 'Approval Condition',
  other: 'Other',
};

const statusConfig: Record<ReviewCommentStatus, { label: string; color: string; icon: typeof AlertCircle }> = {
  open: { label: 'Open', color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'text-yellow-600 bg-yellow-50 border-yellow-200', icon: Clock },
  resolved: { label: 'Resolved', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle2 },
  not_applicable: { label: 'N/A', color: 'text-gray-600 bg-gray-50 border-gray-200', icon: XCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'bg-red-600 text-white' },
  high: { label: 'High', color: 'bg-orange-500 text-white' },
  medium: { label: 'Medium', color: 'bg-yellow-500 text-white' },
  low: { label: 'Low', color: 'bg-gray-400 text-white' },
};

export function ReviewComments({
  comments,
  onAddComment,
  onImportEmail,
  onUpdateStatus,
  onAssign,
  onEdit,
  onDelete,
  teamMembers = [],
  readOnly = false,
}: ReviewCommentsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReviewCommentStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<ReviewCommentSource | 'all'>('all');
  const [expandedCycles, setExpandedCycles] = useState<Set<number>>(new Set([1]));

  // Group comments by review cycle
  const commentsByCycle = useMemo(() => {
    const grouped: Record<number, ReviewComment[]> = {};
    comments.forEach((comment) => {
      const cycle = comment.reviewCycle || 1;
      if (!grouped[cycle]) {
        grouped[cycle] = [];
      }
      grouped[cycle].push(comment);
    });
    return grouped;
  }, [comments]);

  // Filter comments
  const filteredComments = useMemo(() => {
    return comments.filter((comment) => {
      if (statusFilter !== 'all' && comment.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && comment.source !== sourceFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          comment.originalText.toLowerCase().includes(query) ||
          comment.summary?.toLowerCase().includes(query) ||
          comment.pageReference?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [comments, statusFilter, sourceFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const open = comments.filter((c) => c.status === 'open').length;
    const inProgress = comments.filter((c) => c.status === 'in_progress').length;
    const resolved = comments.filter((c) => c.status === 'resolved').length;
    return { open, inProgress, resolved, total: comments.length };
  }, [comments]);

  const toggleCycle = (cycle: number) => {
    setExpandedCycles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cycle)) {
        newSet.delete(cycle);
      } else {
        newSet.add(cycle);
      }
      return newSet;
    });
  };

  const renderComment = (comment: ReviewComment) => {
    const SourceIcon = sourceIcons[comment.source];
    const StatusIcon = statusConfig[comment.status].icon;

    return (
      <div
        key={comment.id}
        className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className="flex items-center gap-1 shrink-0">
                <SourceIcon className="h-3 w-3" />
                {sourceLabels[comment.source]}
              </Badge>
              <Badge variant="secondary" className="text-xs shrink-0">
                {categoryLabels[comment.category]}
              </Badge>
              {comment.priority && (
                <Badge className={`text-xs ${priorityConfig[comment.priority]?.color || ''}`}>
                  {priorityConfig[comment.priority]?.label || comment.priority}
                </Badge>
              )}
              <div
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${statusConfig[comment.status].color}`}
              >
                <StatusIcon className="h-3 w-3" />
                {statusConfig[comment.status].label}
              </div>
            </div>

            {/* Comment text */}
            <p className="text-sm mb-2">{comment.originalText}</p>

            {/* Reference info */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {comment.pageReference && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {comment.pageReference}
                </span>
              )}
              {comment.sheetReference && (
                <span>Sheet: {comment.sheetReference}</span>
              )}
              {comment.assignedToName && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {comment.assignedToName}
                </span>
              )}
              {comment.dueDate && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Due: {new Date(comment.dueDate).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Status updates */}
                <DropdownMenuItem
                  disabled={comment.status === 'open'}
                  onClick={() => onUpdateStatus?.(comment.id, 'open')}
                >
                  <AlertCircle className="h-4 w-4 mr-2 text-red-600" />
                  Mark Open
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={comment.status === 'in_progress'}
                  onClick={() => onUpdateStatus?.(comment.id, 'in_progress')}
                >
                  <Clock className="h-4 w-4 mr-2 text-yellow-600" />
                  Mark In Progress
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={comment.status === 'resolved'}
                  onClick={() => onUpdateStatus?.(comment.id, 'resolved')}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                  Mark Resolved
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onEdit?.(comment)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete?.(comment.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Review Comments
            </CardTitle>
            <CardDescription>
              Track and resolve comments from permit reviewers
            </CardDescription>
          </div>
          {!readOnly && (onAddComment || onImportEmail) && (
            <div className="flex items-center gap-2">
              {onImportEmail && (
                <Button size="sm" variant="outline" onClick={onImportEmail} className="gap-1">
                  <Mail className="h-4 w-4" />
                  Import Email
                </Button>
              )}
              {onAddComment && (
                <Button size="sm" onClick={onAddComment} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add Comment
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        {stats.total > 0 && (
          <div className="flex items-center gap-4 mt-3 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-medium">{stats.total}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-muted-foreground">Open:</span>
              <span className="font-medium">{stats.open}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="text-muted-foreground">In Progress:</span>
              <span className="font-medium">{stats.inProgress}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-muted-foreground">Resolved:</span>
              <span className="font-medium">{stats.resolved}</span>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filters */}
        {comments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search comments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as ReviewCommentStatus | 'all')}
            >
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="not_applicable">N/A</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={sourceFilter}
              onValueChange={(v) => setSourceFilter(v as ReviewCommentSource | 'all')}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {Object.entries(sourceLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Comments list */}
        {comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No review comments yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add comments from permit reviewers to track corrections and responses
            </p>
            {!readOnly && onAddComment && (
              <Button variant="outline" size="sm" onClick={onAddComment}>
                <Plus className="h-4 w-4 mr-1" />
                Add First Comment
              </Button>
            )}
          </div>
        ) : filteredComments.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <p>No comments match your filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.keys(commentsByCycle)
              .sort((a, b) => Number(b) - Number(a))
              .map((cycleStr) => {
                const cycle = Number(cycleStr);
                const cycleComments = commentsByCycle[cycle].filter((c) =>
                  filteredComments.includes(c)
                );
                if (cycleComments.length === 0) return null;

                const isExpanded = expandedCycles.has(cycle);
                const openCount = cycleComments.filter((c) => c.status === 'open').length;

                return (
                  <Collapsible key={cycle} open={isExpanded}>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-between px-3 hover:bg-muted"
                        onClick={() => toggleCycle(cycle)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span className="font-medium">Review Cycle {cycle}</span>
                          <Badge variant="secondary">{cycleComments.length} comments</Badge>
                        </div>
                        {openCount > 0 && (
                          <Badge variant="destructive" className="shrink-0">
                            {openCount} open
                          </Badge>
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-2 mt-2 pl-6">
                        {cycleComments.map(renderComment)}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReviewComments;
